import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import WissenstilColourLogo from "../assets/Wissenstil Colour Logo.svg";     // COLOUR LOGO
import WissenstilWhiteLogo from "../assets/Wissenstil White Logo.svg";      // WHITE LOGO

const Header = () => {
  const [isOnHero, setIsOnHero] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  /* -------- Scroll triggers Header color -------- */
  useEffect(() => {
    let observer;
    const heroSection = document.querySelector("section");

    // If we're on HOME page
    if (location.pathname === "/") {
      // Set initial state based on scroll position
      if (window.scrollY <= 10) {
        setIsOnHero(true);
      } else {
        setIsOnHero(false);
      }

      if (heroSection) {
        observer = new IntersectionObserver(
          ([entry]) => setIsOnHero(entry.isIntersecting),
          { threshold: 0.6 }
        );
        observer.observe(heroSection);
      }
    } else {
      // Any other page: always solid header
      setIsOnHero(false);
    }

    return () => {
      if (observer && heroSection) observer.unobserve(heroSection);
    };
  }, [location.pathname]);

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 w-full z-50 transition-all duration-500
          ${isOnHero
            ? "bg-[rgba(255,255,255,0)] text-white backdrop-blur-0"
            : "bg-white text-[#4f0594] shadow-[0_8px_25px_rgba(0,0,0,0.18)]"
          }
        `}
      >
        <div className="w-full flex items-center justify-between h-18 md:h-24 px-6 md:px-10">

          <Link to="/" className="flex items-center">
            {/* 🔥 WHITE LOGO on hero, COLOUR LOGO after scroll */}
            <img
              src={isOnHero ? WissenstilWhiteLogo : WissenstilColourLogo}
              alt="WissenstilLogo"
              className="h-11 transition-all duration-500"
            />
          </Link>

          {/* DESKTOP MENU */}
          <nav className="desktop-menu hidden md:block">
            <ul
              className={`flex space-x-8 transition-colors duration-500 ${
                isOnHero ? "text-white" : "text-[#6F7DFF]"
              }`}
            >
              <li><Link to="/about">About Us</Link></li>

              <li className="relative group">
                <Link to="/services" className="cursor-pointer">Wise Services</Link>
                <ul className="absolute left-0 mt-3 w-64 bg-[#4f0594]/60 backdrop-blur-xl
                  border border-white/20 rounded-2xl shadow-lg opacity-0 invisible
                  group-hover:opacity-100 group-hover:visible transition-all duration-500 py-4 space-y-2">
                  <li><Link to="/analysis" className="block px-5 py-2 text-base text-[#A8C7FF] hover:text-white">Image Analysis</Link></li>
                  <li><Link to="/detection" className="block px-5 py-2 text-base text-[#A8C7FF] hover:text-white">Anomaly Detection</Link></li>
                  <li><Link to="/medical" className="block px-5 py-2 text-base text-[#A8C7FF] hover:text-white">Medical Coding</Link></li>
                </ul>
              </li>

              <li className="relative group">
                <Link to="/products" className="cursor-pointer">Wise Products</Link>
                <ul className="absolute left-0 mt-3 w-56 bg-[#4f0594]/60 backdrop-blur-xl
                  border border-white/20 rounded-2xl shadow-lg opacity-0 invisible
                  group-hover:opacity-100 group-hover:visible transition-all duration-500 py-3 space-y-1">
                  <li><Link to="/his" className="block px-4 py-2 text-base text-[#A8C7FF] hover:text-white">HIS</Link></li>
                  <li><Link to="/ehr" className="block px-4 py-2 text-base text-[#A8C7FF] hover:text-white">EHR</Link></li>
                  <li><Link to="/rcm" className="block px-4 py-2 text-base text-[#A8C7FF] hover:text-white">RCM</Link></li>
                </ul>
              </li>

              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </nav>

          {/* HAMBURGER */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="menu-btn p-2 rounded-lg ml-4"
          >
            <div className="space-y-1 transition-all duration-300">
              <span
                className={`block w-8 h-[4px] transition-all duration-300
                  ${isOnHero ? "bg-white" : "bg-[#6F7DFF]"}
                  ${menuOpen ? "rotate-45 translate-y-2" : ""}`}
              />
              <span
                className={`block w-8 h-[4px] transition-all duration-300
                  ${isOnHero ? "bg-white" : "bg-[#6F7DFF]"}
                  ${menuOpen ? "opacity-0" : ""}`}
              />
              <span
                className={`block w-8 h-[4px] transition-all duration-300
                  ${isOnHero ? "bg-white" : "bg-[#6F7DFF]"}
                  ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}
              />
            </div>
          </button>
        </div>
      </header>

      {/* MOBILE SIDEBAR */}
      <div
        className={`fixed top-[6rem] right-6 max-h-[92vh] w-96 overflow-y-auto
          bg-[#4f0594]/40 backdrop-blur-xl border border-white/30
          rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.35)] text-white
          z-[999] transition-all duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)]
          transform origin-right ${menuOpen ? "translate-x-0 opacity-100" : "translate-x-[120%] opacity-0"}
        `}
      >
        <div className="py-10 px-6 space-y-10 text-lg">
          <Link to="/about" onClick={() => setMenuOpen(false)} className="block text-2xl text-[#A8C7FF] hover:text-white">About Us</Link>

          {/* MOBILE — WISE SERVICES */}
          <details className="group">
            <summary className="cursor-pointer flex justify-between items-center text-2xl text-[#A8C7FF] hover:text-white">
              <Link to="/services" onClick={() => setMenuOpen(false)}>Wise Services</Link>
              <span className="transition-transform duration-300 group-open:rotate-180">▾</span>
            </summary>
            <div className="ml-4 mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
              <Link to="/analysis" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">Image Analysis</Link>
              <Link to="/detection" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">Anomaly Detection</Link>
              <Link to="/medical" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">Medical Coding</Link>
            </div>
          </details>

          {/* MOBILE — WISE PRODUCTS */}
          <details className="group">
            <summary className="cursor-pointer flex justify-between items-center text-2xl text-[#A8C7FF] hover:text-white">
              <Link to="/products" onClick={() => setMenuOpen(false)}>Wise Products</Link>
              <span className="transition-transform duration-300 group-open:rotate-180">▾</span>
            </summary>
            <div className="ml-4 mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
              <Link to="/his" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">HIS</Link>
              <Link to="/ehr" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">EHR</Link>
              <Link to="/rcm" onClick={() => setMenuOpen(false)} className="block text-base text-[#A8C7FF] hover:text-white">RCM</Link>
            </div>
          </details>

          <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-2xl text-[#A8C7FF] hover:text-white">Contact Us</Link>
        </div>
      </div>

      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30" />
      )}
    </>
  );
};

export default Header;